/**
 * Centralized API Configuration
 * 
 * This file provides a consistent way to configure API endpoints
 * that works for both development and production environments.
 */
import axios from 'axios';

/**
 * Get the API base URL for the current environment
 * In development: uses environment variable or defaults to localhost
 * In production: uses relative paths (assumes API is served from same domain)
 */
export const getApiBaseUrl = (): string => {
  // Use environment variable if provided
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // In production, use relative paths (same domain)
  if (process.env.NODE_ENV === 'production') {
    return '/api';
  }
  
  // Development fallback - updated to match backend default port 3200
  return 'http://localhost:3200/api';
};

// Export the API base URL
export const API_BASE_URL = getApiBaseUrl();

/**
 * Create a pre-configured axios instance with proper base URL and credentials
 */
export const createApiClient = () => {
  const client = axios.create({
    baseURL: API_BASE_URL,
    withCredentials: true, // Include cookies in requests
    timeout: 30000, // 30 second timeout
  });

  return client;
};

/**
 * Get authentication token from localStorage
 */
export const getAuthToken = (): string | null => {
  return localStorage.getItem('auth_token');
};

/**
 * Set authentication header on an axios instance
 */
export const setAuthHeader = (client: any): void => {
  const token = getAuthToken();
  
  // Ensure headers structure exists
  if (!client.defaults) {
    client.defaults = {};
  }
  if (!client.defaults.headers) {
    client.defaults.headers = {};
  }
  if (!client.defaults.headers.common) {
    client.defaults.headers.common = {};
  }
  
  if (token) {
    client.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete client.defaults.headers.common['Authorization'];
  }
};

/**
 * Create an authenticated API client with auth headers set
 */
export const createAuthenticatedApiClient = () => {
  const client = createApiClient();
  setAuthHeader(client);
  return client;
};

/**
 * Default configured API client instance
 */
export const apiClient = createApiClient(); 